/**
 * /downhole/wells/:id — one Directional & MWD well job (api#1055,
 * `GET /downhole/jobs/:id`). Gated by DdModeGate.
 *
 * Sections, in the order the brief gives them: the well's identity, the
 * Timeline (the four stamps, each correctable once reached), the Crew
 * (api#1057, WellCrew.tsx), the Days (api#1062, WellDays.tsx), the job's
 * Invoicing (api#1090, WellBilling.tsx), the assemblies on the job, and their runs.
 *
 * WRITES are DOWNHOLE_JOB_WRITE_ROLES (admin, office_staff, dispatcher). A
 * field_worker reads the job with no controls. A CLOSED job (completed,
 * cancelled) takes notes only, so the step, cancel, stamp corrections and the
 * assembly controls are not offered there; Edit stays, for the notes.
 *
 * The assembly rows hold a Remove button, so they are NOT row targets (the
 * Rate Sheets rule): the assembly number is the link, the button is a button.
 */
import React, { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Layers, Loader2, Pencil, Plus, Trash2, CalendarClock, ClipboardList, Truck } from 'lucide-react';
import { toast } from 'sonner';
import { getApiErrorMessage } from '../../api/client';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import { useAuth } from '../../contexts/AuthContext';
import { displayValue } from '../../features/fieldiq-handoff/contracts';
import { useDeleteJob, useDetachJobAssembly, useDownholeJob } from '../../hooks/useDownholeJobs';
import { formatCalendarDate, formatInstant, formatInstantDay } from '../../lib/calendar-date';
import { fmtHours, fmtQty } from '../../lib/display-labels';
import { canCancelJob, canDeleteJob, DH_JOB_STAMP_LABEL, isJobClosed, nextJobStep } from '../../lib/downhole-jobs';
import { DH_ASSEMBLY_STATUS_SKIN, DH_JOB_STATUS_SKIN } from '../../lib/downhole-labels';
import { onlineOrSay } from '../../lib/downhole-offline';
import { canWriteDownholeInvoices, canWriteDownholeJobs } from '../../lib/downhole-roles';
import { formatDh } from '../../lib/downhole-units';
import { queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import { regionName } from '../../components/ProvinceSelect';
import { DH_JOB_STAMP_COLUMNS, type DhJobDetail, type DhJobStampColumn, type DhUnitSystem } from '../../types/downhole';
import { DdModeGate } from './DdModeGate';
import { DH_SECONDARY, DhBadge, DhDialog, DhEmpty, DhPanel, DhSpec, RunStatePill } from './DownholeBits';
import { AttachAssemblyDialog, CancelJobDialog, JobFormDialog, StampDialog, StepDialog } from './WellJobDialogs';
import { CrewPanel } from './WellCrew';
import { DaysPanel } from './WellDays';
import { BillingPanel } from './WellBilling';
import { canWriteDownholeTickets } from './ticketBits';

/**
 * Band figures are month and day only: with the year, a date is wider than a
 * phone's stat column and is cut off at 360px (measured). The full dates are in
 * the body, in Identity and the Timeline.
 */
const BAND_DAY: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };

/** A stamp is a record: it carries its year. */
const STAMP_FORMAT: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true };

/** A secondary action on the band: white/25 border and no colour class (CLAUDE.md, the band). */
const BAND_SECONDARY =
  'inline-flex h-9 items-center gap-1.5 rounded-fi-control border border-white/25 px-3 text-sm font-bold transition hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-50';

export default function WellDetailScreen() {
  return (
    <DdModeGate>
      <WellDetail />
    </DdModeGate>
  );
}

function WellDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const query = useDownholeJob(id, true);
  const failure = queryLoadFailure(query);
  const job = query.data?.data ?? null;
  const tz = query.data?.meta?.time_zone ?? null;
  const units = query.data?.meta?.unit_system ?? null;

  if (failure) {
    return (
      <div className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)]">
        <LoadFailureNotice error={queryFailureError(query)} kind={failure} subject="this well job" />
      </div>
    );
  }
  if (query.isLoading || !job) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="size-6 animate-spin text-fieldiq-muted" aria-label="Loading well job" />
      </div>
    );
  }
  return (
    <WellDetailBody
      job={job}
      tz={tz}
      units={units}
      canWrite={canWriteDownholeJobs(user?.role)}
      canTicket={canWriteDownholeTickets(user?.role)}
      canInvoice={canWriteDownholeInvoices(user?.role)}
      onDeleted={() => navigate('/downhole/wells')}
    />
  );
}

function WellDetailBody({ job, tz, units, canWrite, canTicket, canInvoice, onDeleted }: {
  job: DhJobDetail;
  tz: string | null;
  units: DhUnitSystem | null;
  canWrite: boolean;
  /** DOWNHOLE_TICKET_WRITE_ROLES: opens a day's ticket. */
  canTicket: boolean;
  /** The one invoice door's roles (admin, office_staff): bills the signed days. */
  canInvoice: boolean;
  onDeleted: () => void;
}) {
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);
  const [stepping, setStepping] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [attaching, setAttaching] = useState(false);
  const [stamp, setStamp] = useState<DhJobStampColumn | null>(null);
  const detach = useDetachJobAssembly();
  const del = useDeleteJob();

  const closed = isJobClosed(job.status);
  const step = canWrite ? nextJobStep(job.status) : null;
  const identifier = job.uwi || job.api_number || null;

  const removeAssembly = (assemblyId: string, number: string) => {
    if (!onlineOrSay((m) => toast.error(m))) return;
    detach.mutate(
      { id: job.id, assembly_id: assemblyId },
      {
        onSuccess: () => toast.success(`${number} taken off the job`),
        onError: (err) => toast.error(getApiErrorMessage(err, 'The assembly could not be taken off the job.')),
      },
    );
  };

  return (
    <div data-testid="downhole-well-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow={`Well job · ${job.job_number}`}
        title={job.well_name || job.job_number}
        onBack={() => navigate('/downhole/wells')}
        backLabel="Back to wells"
        actions={
          canWrite ? (
            <div className="flex flex-wrap gap-2">
              {step && (
                <button type="button" onClick={() => setStepping(true)} className="fi-action fi-action--standard fi-action--primary">
                  {step.label}
                </button>
              )}
              <button type="button" onClick={() => setEditing(true)} className={BAND_SECONDARY}>
                <Pencil className="size-4" aria-hidden="true" />
                Edit
              </button>
              {canCancelJob(job.status) && (
                <button type="button" onClick={() => setCancelling(true)} className={BAND_SECONDARY}>Cancel job</button>
              )}
              {canDeleteJob(job.status, job.assembly_count) && (
                <button type="button" onClick={() => setDeleting(true)} className={BAND_SECONDARY}>
                  <Trash2 className="size-4" aria-hidden="true" />
                  Delete
                </button>
              )}
            </div>
          ) : undefined
        }
        stats={[
          { icon: <Layers className="size-4" aria-hidden="true" />, label: 'Assemblies', sub: 'On this job', tone: 'accent', value: fmtQty(job.assembly_count) },
          { icon: <ClipboardList className="size-4" aria-hidden="true" />, label: 'Runs', sub: job.run_open === true ? 'One in the hole' : 'Logged', tone: 'info', value: fmtQty(job.run_count) },
          { icon: <CalendarClock className="size-4" aria-hidden="true" />, label: 'Start', sub: 'Scheduled', tone: 'neutral', value: formatCalendarDate(job.scheduled_start, BAND_DAY) ?? '—' },
          // Four stats, not three: the band's circle clearance was measured on a
          // four-column strip, and a three-column strip puts column 3 under the
          // circle's edge (measured here, 1024-2560: label, figure and sub-line all crossed it).
          { icon: <Truck className="size-4" aria-hidden="true" />, label: 'Released', sub: 'Rig released', tone: 'success', value: formatInstantDay(job.released_at, tz, BAND_DAY) ?? '—' },
        ]}
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          <DhPanel eyebrow="Well" title="Identity" testId="well-identity" actions={<DhBadge map={DH_JOB_STATUS_SKIN} value={job.status} />}>
            <div className="grid gap-4 px-4 py-4 sm:grid-cols-2 lg:grid-cols-4">
              <DhSpec label="Customer" value={displayValue(job.customer_name)} />
              <DhSpec label="UWI / API" value={displayValue(identifier)} mono />
              <DhSpec label="LSD" value={displayValue(job.lsd)} mono />
              <DhSpec label="Licence" value={displayValue(job.well_licence)} mono />
              <DhSpec label="AFE" value={displayValue(job.afe_number)} />
              <DhSpec label="PO" value={displayValue(job.po_number)} />
              <DhSpec label="Rig" value={displayValue(job.rig_name)} />
              <DhSpec label="Province / state" value={job.province_state ? regionName(job.province_state) || job.province_state : '—'} />
              <DhSpec label="Scheduled start" value={formatCalendarDate(job.scheduled_start) ?? '—'} />
              {/* api#1062. Absent (a read from before batch 4) is unknown, not "No". */}
              <DhSpec label="Footage billing" value={job.footage_billing === undefined ? '—' : job.footage_billing ? 'Yes, per day' : 'No'} />
            </div>
            {job.status === 'cancelled' && (
              <p className="border-t border-fieldiq-border px-4 py-3 text-sm text-fieldiq-text">
                <span className="font-bold">Cancelled:</span> {displayValue(job.cancel_reason)}
              </p>
            )}
            {job.notes && <p className="whitespace-pre-wrap border-t border-fieldiq-border px-4 py-3 text-sm text-fieldiq-text">{job.notes}</p>}
          </DhPanel>

          <DhPanel eyebrow="Steps" title="Timeline" testId="well-timeline">
            <ol className="divide-y divide-fieldiq-border">
              {DH_JOB_STAMP_COLUMNS.map((col) => {
                const at = job[col];
                return (
                  <li key={col} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3" data-testid={`stamp-${col}`}>
                    <div>
                      <p className="text-sm font-bold text-white">{DH_JOB_STAMP_LABEL[col]}</p>
                      <p className="text-sm text-fieldiq-text">{at ? formatInstant(at, tz, STAMP_FORMAT) ?? '—' : 'Not reached'}</p>
                    </div>
                    {canWrite && !closed && at && (
                      <button type="button" className={DH_SECONDARY} onClick={() => setStamp(col)} aria-label={`Correct ${DH_JOB_STAMP_LABEL[col].toLowerCase()} time`}>
                        Correct
                      </button>
                    )}
                  </li>
                );
              })}
            </ol>
          </DhPanel>

          <CrewPanel job={job} tz={tz} canWrite={canWrite} />

          <DaysPanel job={job} units={units} canWriteDays={canWrite} canTicket={canTicket} />

          {/* api#1090. The billing read is the office roles (admin, office_staff,
              dispatcher), the same set as job writes, so a field worker never
              asks for it and never sees a 403 drawn as a panel. */}
          {canWrite && <BillingPanel job={job} tz={tz} canInvoice={canInvoice} />}

          <DhPanel
            eyebrow="Tools"
            title="Assemblies"
            testId="well-assemblies"
            actions={
              canWrite && !closed ? (
                <button type="button" className={DH_SECONDARY} onClick={() => setAttaching(true)}>
                  <Plus className="size-4" aria-hidden="true" />
                  Add assembly
                </button>
              ) : undefined
            }
          >
            {job.assemblies.length === 0 ? (
              <DhEmpty>No assembly is on this job yet.</DhEmpty>
            ) : (
              <ul className="divide-y divide-fieldiq-border">
                {job.assemblies.map((a) => (
                  <li key={a.id} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
                    <div className="min-w-0">
                      <Link to={`/downhole/assemblies/${a.id}/build`} className="font-mono text-sm font-extrabold text-fieldiq-accent-signal underline-offset-2 hover:underline">
                        {a.assembly_number}
                      </Link>
                      <p className="truncate text-sm text-fieldiq-text">{a.name}</p>
                      <p className="text-xs text-fieldiq-muted">
                        {fmtQty(a.component_count)} tools · {formatDh(a.total_length_ft, 'length', units)} · {fmtQty(a.run_count)} runs
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <DhBadge map={DH_ASSEMBLY_STATUS_SKIN} value={a.status} />
                      {canWrite && !closed && (
                        <button
                          type="button"
                          className={DH_SECONDARY}
                          disabled={detach.isPending}
                          onClick={() => removeAssembly(a.id, a.assembly_number)}
                          aria-label={`Take ${a.assembly_number} off the job`}
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </DhPanel>

          <DhPanel eyebrow="Run logs" title="Runs" testId="well-runs">
            {job.runs.length === 0 ? (
              <DhEmpty>No run is logged on this job yet.</DhEmpty>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[880px] text-left">
                  <thead className="border-b border-fieldiq-border bg-fieldiq-dark/40 text-[0.64rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">
                    <tr>
                      <th scope="col" className="px-4 py-3">Run in</th>
                      <th scope="col" className="px-4 py-3">Assembly</th>
                      <th scope="col" className="px-4 py-3">Run</th>
                      <th scope="col" className="px-4 py-3 text-right">Depth out</th>
                      <th scope="col" className="px-4 py-3 text-right">Footage</th>
                      <th scope="col" className="px-4 py-3 text-right">Circ. hrs</th>
                      <th scope="col" className="px-4 py-3" />
                    </tr>
                  </thead>
                  <tbody>
                    {job.runs.map((r) => (
                      <tr key={r.id} className="border-t border-fieldiq-border">
                        <td className="whitespace-nowrap px-4 py-3 text-sm text-fieldiq-text">{formatInstantDay(r.run_in_at, tz) ?? '—'}</td>
                        <td className="whitespace-nowrap px-4 py-3 font-mono text-sm font-extrabold text-fieldiq-accent-signal">{r.assembly_number || '—'}</td>
                        <td className="whitespace-nowrap px-4 py-3 text-sm text-fieldiq-text">{displayValue(r.run_number)} <RunStatePill state={r.run_state} /></td>
                        <td className="whitespace-nowrap px-4 py-3 text-right font-mono text-sm text-fieldiq-text">{formatDh(r.depth_out_ft, 'length', units, { digits: 0 })}</td>
                        <td className="whitespace-nowrap px-4 py-3 text-right font-mono text-sm text-fieldiq-text">{formatDh(r.footage_drilled, 'length', units, { digits: 0 })}</td>
                        <td className="px-4 py-3 text-right font-mono text-sm tabular-nums text-fieldiq-text">{fmtHours(r.circulating_hours, 1)}</td>
                        <td className="px-4 py-3 text-right">
                          <Link to={`/downhole/run-logs/${r.id}`} className="text-sm font-bold text-fieldiq-accent-signal underline-offset-2 hover:underline">
                            Open
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </DhPanel>
        </div>
      </section>

      {canWrite && (
        <>
          <JobFormDialog open={editing} job={job} onClose={() => setEditing(false)} onSaved={() => undefined} />
          {step && <StepDialog open={stepping} job={job} step={step} onClose={() => setStepping(false)} />}
          {canCancelJob(job.status) && <CancelJobDialog open={cancelling} job={job} onClose={() => setCancelling(false)} />}
          {stamp && <StampDialog open job={job} stamp={stamp} timeZone={tz} onClose={() => setStamp(null)} />}
          {!closed && <AttachAssemblyDialog open={attaching} job={job} onClose={() => setAttaching(false)} />}
          <DhDialog
            open={deleting}
            title={`Delete ${job.job_number}`}
            onClose={() => setDeleting(false)}
            footer={
              <>
                <button type="button" className={DH_SECONDARY} onClick={() => setDeleting(false)} disabled={del.isPending}>Keep it</button>
                <button
                  type="button"
                  className="fi-action fi-action--standard fi-action--primary"
                  disabled={del.isPending}
                  onClick={() => {
                    if (!onlineOrSay((m) => toast.error(m))) return;
                    del.mutate(job.id, {
                      onSuccess: () => {
                        toast.success(`${job.job_number} deleted`);
                        onDeleted();
                      },
                      onError: (err) => toast.error(getApiErrorMessage(err, 'The well job could not be deleted.')),
                    });
                  }}
                >
                  {del.isPending && <Loader2 className="size-4 animate-spin" aria-hidden="true" />}
                  Delete well job
                </button>
              </>
            }
          >
            <p className="text-sm text-fieldiq-text">This removes {job.job_number} from the well register.</p>
          </DhDialog>
        </>
      )}
    </div>
  );
}
